import {useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {PRESENTATIONS_LIST_PAGE_SIZE_DEFAULT_VALUES, SORT_DEFAULT} from '../../../constants';
import {useListParamsState} from '../../../hooks/useListParamsState';
import {ListParams, SetListParamsOptions} from '../../../hooks/listParams';
import {IPresentation} from '../../../incase-domain/presentation';
import {fetchPresentationListPaginated} from '../../../service/presentation';
import {setConnectionListPageAction, setConnectionListSearchTermAction} from '../../../store/actions/connection.actions';
import {connectionListPageSelector, connectionListSearchTermSelector} from '../../../store/selectors/connection.selectors';
import {useConnectionsPaginatedQuery, toPaginatedResultWrapper} from '../../../hooks/connectionQueries';
import CreatePresentationLinksModals from './presentationLinks/CreatePresentationLinksModals';
import PresentationListModals from './PresentationListModals';
import PresentationListView from './PresentationListView';
import {usePresentationsListSearchParams} from './presentationsListSearchParams';

interface PresentationListProps {
    paginated?: boolean;
    onCreatePresentation: () => void;
    onImportPresentation: () => void;
    onImportHtml?: () => void;
}

interface PresentationListBodyProps extends PresentationListProps {
    listParams: ListParams;
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void;
    listQueryKey: ReturnType<typeof QUERY_KEYS.presentation.list>;
}

function PresentationListBody(props: PresentationListBodyProps): JSX.Element {
    const {paginated = false, onCreatePresentation, onImportPresentation, onImportHtml, listParams, setListParams, listQueryKey} = props;

    const dispatch = useDispatch();
    const client = useQueryClient();

    const connectionListSearchTerm = useSelector(connectionListSearchTermSelector);
    const connectionListPage = useSelector(connectionListPageSelector);
    const connectionsPaginatedSearch = useMemo(
        () => ({
            searchString: connectionListSearchTerm,
            pageNumber: connectionListPage,
            pageSize: 5,
        }),
        [connectionListSearchTerm, connectionListPage]
    );
    const connectionsQuery = useConnectionsPaginatedQuery(connectionsPaginatedSearch);
    const connections = toPaginatedResultWrapper(connectionsPaginatedSearch, connectionsQuery.data, connectionsQuery.isFetching);

    const [selected, setSelected] = useState<IPresentation[]>([]);
    const [deletePresentations, setDeletePresentations] = useState(false);
    const [presentationToDelete, setPresentationToDelete] = useState<IPresentation[]>([]);
    const [selectedPresentationForLinks, setSelectedPresentationForLinks] = useState<IPresentation>();
    const [isLinksModalOpen, setIsLinksModalOpen] = useState(false);
    const [sendPresentationModal, setSendPresentationModal] = useState(false);
    const [selectedForRoomCreation, setSelectedForRoomCreation] = useState<IPresentation | undefined>();

    const fetchedPresentationList = useQuery(
        listQueryKey,
        () => fetchPresentationListPaginated(listParams.page, listParams.size, listParams.search, listParams.sortBy, false, true),
        {keepPreviousData: true, refetchOnWindowFocus: false, refetchOnMount: true, staleTime: 5 * 60 * 1000}
    );

    function onDeletePageChangeHandler(deletedCount: number): void {
        let newElCount = 0;
        let newMaxPage = 0;
        if (fetchedPresentationList.data) {
            newElCount = fetchedPresentationList.data.numberOfElements - deletedCount;
            newMaxPage = Math.max(Math.ceil(newElCount / fetchedPresentationList.data.pageSize), 1);
        }

        if (fetchedPresentationList.data && listParams.page > newMaxPage) {
            setListParams({page: newMaxPage});
            client.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
        }
    }

    const onSearch = (searchTerm: string): void => {
        dispatch(setConnectionListPageAction(1));
        dispatch(setConnectionListSearchTermAction(searchTerm));
    };

    const openLinksModal = (presentation: IPresentation): void => {
        setSelectedPresentationForLinks(presentation);
        setIsLinksModalOpen(true);
    };

    return (
        <>
            {fetchedPresentationList.data && (
                <PresentationListView
                    presentationList={fetchedPresentationList}
                    listParams={listParams}
                    setListParams={setListParams}
                    setDeletePresentations={setDeletePresentations}
                    setPresentationToDelete={setPresentationToDelete}
                    setSendPresentationModal={setSendPresentationModal}
                    setSelectedPresentationForLinks={openLinksModal}
                    setSelected={setSelected}
                    setSelectedForRoomCreation={setSelectedForRoomCreation}
                    onCreatePresentation={onCreatePresentation}
                    onImportPresentation={onImportPresentation}
                    onImportHtml={onImportHtml}
                    enablePagination={paginated}
                />
            )}
            <PresentationListModals
                deletePresentations={deletePresentations}
                setDeletePresentations={setDeletePresentations}
                selected={selected}
                setSelected={setSelected}
                presentationToDelete={presentationToDelete}
                setPresentationToDelete={setPresentationToDelete}
                sendPresentationModal={sendPresentationModal}
                setSendPresentationModal={setSendPresentationModal}
                onSearch={onSearch}
                onDeletePageChangeHandler={onDeletePageChangeHandler}
                connections={connections}
                selectedForRoomCreation={selectedForRoomCreation}
                setSelectedForRoomCreation={setSelectedForRoomCreation}
            />
            {selectedPresentationForLinks && (
                <CreatePresentationLinksModals
                    open={isLinksModalOpen}
                    closeModal={() => setIsLinksModalOpen(false)}
                    presentation={selectedPresentationForLinks}
                />
            )}
        </>
    );
}

function PresentationListWithUrlParams(props: PresentationListProps): JSX.Element {
    const {listParams, setListParams} = usePresentationsListSearchParams();

    return (
        <PresentationListBody
            {...props}
            paginated
            listParams={listParams}
            setListParams={setListParams}
            listQueryKey={QUERY_KEYS.presentation.list(listParams)}
        />
    );
}

function PresentationListWithLocalState(props: PresentationListProps): JSX.Element {
    const {listParams, setListParams} = useListParamsState({
        size: PRESENTATIONS_LIST_PAGE_SIZE_DEFAULT_VALUES.initialPageSize,
        sortBy: SORT_DEFAULT,
    });

    return (
        <PresentationListBody
            {...props}
            listParams={listParams}
            setListParams={setListParams}
            listQueryKey={QUERY_KEYS.presentation.list(listParams)}
        />
    );
}

function PresentationList(props: PresentationListProps): JSX.Element {
    const {paginated = false} = props;

    if (paginated) {
        return <PresentationListWithUrlParams {...props} />;
    }

    return <PresentationListWithLocalState {...props} />;
}

export default PresentationList;
